import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import union from 'lodash/union';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import StructureModel from '@Components/Results/structure/structure-model.service';
import ShowMore from '@Components/Results/utils/ShowMore';
import StructureZoomService from '@Services/StructureZoom';
import Structure from '@Components/Results/structure/Structure';
import * as ResultsDataManage from '@Services/ResultsDataManage';
import ResultDetailCard from '@Components/Results/common/ResultDetailCard';
import Breadcrumb from '@Services/Breadcrumb';
import { enrichParamsWithSemanticFlags } from '@Pages/Results/semanticSearchUtils';

import './DocumentSubstances.scss';

function DocumentSubstances({
  metadata,
  documentIndex,
  documentRelativeIndex,
  databaseId,
  substancesCount,
  isHit,
  minCount = 9,
  maxCount = 40,
  zoom,
  closeClick,
}) {
  const [substances, setSubstances] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isShowLess, setIsShowLess] = useState(false);
  const pendingRequestRef = useRef(null);
  const isMountedRef = useRef(true); // Track if the component is mounted
  const zoomClasses = StructureZoomService.getZoomClasses('citations') || [];
  const zoomClass = zoomClasses[zoom];

  const getSubstancesForCitations = () => {
    const currentCount = substances.length;
    const context = 'substances';
    const nextSubstancesCount = currentCount === 0 ? minCount : maxCount;

    setIsLoading(true);

    const activeBreadcrumb = Breadcrumb.getBreadcrumbForDbByResultName(databaseId, metadata?.resultName);
    const paramWithSemantic = enrichParamsWithSemanticFlags(
      {
        resultName: metadata.resultName,
        resultIndex: documentIndex,
        resultRelativeIndex: documentRelativeIndex,
        countOfCurrentItems: currentCount,
        totalItems: substancesCount,
        databaseId,
        sort: metadata.sort,
        order: metadata.order,
        context,
        count: nextSubstancesCount,
        hitData: isHit,
      },
      activeBreadcrumb
    );

    const request = ResultsDataManage.getItemsForCitations(paramWithSemantic);

    pendingRequestRef.current = request;

    return request
      .then((data) => {
        if (!isMountedRef.current) return; // Prevent state updates if unmounted

        if (!data) {
          Logger.error('No results returned for document substances');
          return;
        }

        setSubstances((prevSubstances) =>
          union(
            prevSubstances,
            // eslint-disable-next-line sonarjs/no-nested-functions
            data.map((substance) => new StructureModel({ ...substance, databaseId }))
          )
        );
        setIsLoading(false);
        // eslint-disable-next-line consistent-return
        return data; // Ensure the data is returned
      })
      .catch((e) => {
        if (isMountedRef.current) {
          Logger.error(e);
          setIsLoading(false);
        }
      });
  };

  const showLessItems = () => {
    setSubstances((prevSubstances) => prevSubstances.slice(0, minCount));
  };

  useEffect(() => {
    isMountedRef.current = true; // Mark as mounted
    getSubstancesForCitations();
    Logger.info('DocumentSubstances mounted');
    return () => {
      isMountedRef.current = false; // Mark as unmounted
      const cancel = get(pendingRequestRef.current, 'cancel');
      if (isFunction(cancel)) {
        cancel();
      }
      pendingRequestRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sectionId = isHit ? 'hitSubstancesExpanded' : 'substancesExpanded';
  return (
    <ResultDetailCard
      id={`${sectionId}-content`}
      title={isHit ? 'Hit Substances' : 'Substances'}
      className="u-els-margin-top-1o2"
      closeDataE2e="e2e-substances-close"
      onClose={closeClick}>
      <div className="substance-container u-els-display-flex">
        {substances.map((structure, index) => (
          <div
            className={`substance-item ${zoomClass} u-els-background-color-background`}
            // eslint-disable-next-line react/no-array-index-key
            key={index}>
            <Structure isCard structure={structure} zoom={zoom} metadata={{ ...metadata, fromDocuments: true }} />
          </div>
        ))}
      </div>
      <div className={`rx-btn-group--center u-els-margin-top ${isShowLess ? 'show-less-sticky' : ''}`}>
        {!metadata.isViewPreview && substances.length > 0 && (
          <ShowMore
            hideIcon
            idSuffix={`substances-for-citations-${documentIndex}`}
            labelAll={
              substancesCount - substances.length < 50
                ? `Show All Available ${substancesCount - substances.length}`
                : `Show next ${maxCount}`
            }
            initial={minCount}
            current={substances.length}
            total={substancesCount}
            container={`#${sectionId}-content`}
            moreHandler={() => {
              setIsShowLess(true);
              return getSubstancesForCitations();
            }}
            lessHandler={() => {
              setIsShowLess(false);
              return showLessItems();
            }}
            documentResults
            isLoading={isLoading}
          />
        )}
      </div>
    </ResultDetailCard>
  );
}

DocumentSubstances.propTypes = {
  metadata: MetadataPropTypesShape,
  documentIndex: PropTypes.string.isRequired,
  documentRelativeIndex: PropTypes.string,
  databaseId: PropTypes.string.isRequired,
  substancesCount: PropTypes.number.isRequired,
  isHit: PropTypes.bool,
  closeClick: PropTypes.func.isRequired,
  minCount: PropTypes.number,
  maxCount: PropTypes.number,
  zoom: PropTypes.number,
};

export default DocumentSubstances;
